import { LocalStorageHelper, ObjectToStore } from '../models';

declare global {
	interface Window {
		instantConfigCache?: Record<string, string>;
	}
}

interface StorageAdapter {
	getItem(key: string): string | null;

	setItem(key: string, value: string): void;
}

export class localStorageHelper implements LocalStorageHelper {
	private readonly storageAdapter: StorageAdapter;

	constructor(private readonly key: string, private readonly ttlInMilliseconds = 60000) {
		let localStorageAvailable = false;
		try {
			localStorageAvailable = window.localStorage !== undefined && window.localStorage !== null;
			if (localStorageAvailable) {
				window.localStorage.setItem('__test__', 'test');
				window.localStorage.removeItem('__test__');
			}
		} catch (e) {
			localStorageAvailable = false;
		}

		const fallbackStorageAdapter = {
			getItem: (key: string): string | null => window?.instantConfigCache?.[key] ?? null,
			setItem: (key: string, value: string): void => {
				if (window.instantConfigCache === undefined) {
					window.instantConfigCache = {};
				}
				window.instantConfigCache[key] = value;
			},
		};
		this.storageAdapter = {
			getItem: (key: string): string | null => {
				try {
					if (localStorageAvailable) {
						return window.localStorage.getItem(key) ?? fallbackStorageAdapter.getItem(key);
					}
					return fallbackStorageAdapter.getItem(key);
				} catch (e) {
					return fallbackStorageAdapter.getItem(key);
				}
			},
			setItem: (key: string, value: string): void => {
				try {
					if (localStorageAvailable) {
						return localStorage.setItem(key, value);
					}
					fallbackStorageAdapter.setItem(key, value);
				} catch (e) {
					fallbackStorageAdapter.setItem(key, value);
				}
			},
		};
	}

	set(data: string): void {
		const objectToStore = this.getObjectToStore(data);
		this.storageAdapter.setItem(this.key, JSON.stringify(objectToStore));
	}

	get(): string | null {
		const storedValue: string | undefined = this.storageAdapter.getItem(this.key);
		return !storedValue ? null : this.parseStoredValue(JSON.parse(storedValue));
	}

	private expired(ttl: number | undefined): boolean {
		return ttl ? ttl < Date.now() : true;
	}

	private getObjectToStore(data: string): ObjectToStore {
		return {
			data,
			ttl: Date.now() + this.ttlInMilliseconds,
		};
	}

	private parseStoredValue(storedValue: ObjectToStore): string | null {
		return this.expired(storedValue?.ttl) ? null : storedValue.data;
	}
}
